import React from "react";
import NextImage, { ImageProps } from "next/image";

import { urlForImage } from "../sanity/utils";
import { ImageFromSanity } from "./types";

type Props = {
	image: ImageFromSanity;
	priority?: boolean;
	fallBack?: React.ReactNode;
	width: number;
	height: number;
	dpr?: number;
	isFluid?: boolean;
} & Omit<ImageProps, "src" | "alt" | "width" | "height">;

const EXCLUDED_EXTENSIONS = ["svg", "gif"];

const isAppRelative = (url: string | null | undefined): url is string =>
	Boolean(url?.startsWith("/"));

const SanityImage = ({
	image: source,
	fallBack,
	className,
	width,
	height,
	priority,
	fill,
	objectFit,
	dpr = 2,
	isFluid = false,
	placeholder = "empty",
	loading,
	sizes,
}: Props) => {
	if (!source) {
		return <>{fallBack}</>;
	}

	if (
		isAppRelative(source.url) ||
		(!source.asset?._ref &&
			typeof source.url === "string" &&
			/^https?:\/\//.test(source.url))
	) {
		return (
			<NextImage
				src={source.url}
				// Same layout handling as the Sanity branch below: `fill` and explicit
				// dimensions are mutually exclusive, so a caller that fills must not
				// also receive width and height.
				width={!fill ? width : undefined}
				height={!fill ? height : undefined}
				fill={fill}
				objectFit={objectFit}
				sizes={sizes}
				alt="Sanity Image"
				className={className}
				priority={priority}
				placeholder={placeholder}
				loading={loading}
				unoptimized
			/>
		);
	}

	if (source.extension && EXCLUDED_EXTENSIONS.includes(source?.extension)) {
		return (
			<NextImage
				src={source.url ?? ""}
				width={width}
				height={height}
				alt="Sanity Image"
				className={className}
				priority={priority}
				placeholder={placeholder}
				loading={loading}
			/>
		);
	}

	const image = source?.asset?._ref;

	if (!image) {
		return <>{fallBack}</>;
	}

	const imageBuilder = urlForImage(source);

	if (!imageBuilder) {
		return <>{fallBack}</>;
	}

	// The image builder now respects hotspot and crop data automatically
	const imageSrc = isFluid
		? imageBuilder.width(width).quality(100).dpr(dpr).url()
		: imageBuilder.height(height).width(width).quality(100).dpr(dpr).url();

	return (
		<NextImage
			width={!fill ? width : undefined}
			height={!fill ? height : undefined}
			className={className}
			src={imageSrc}
			alt="Sanity Image"
			priority={priority}
			fill={fill}
			objectFit={objectFit}
			placeholder={placeholder}
			loading={loading}
			quality={100}
			sizes={sizes}
		/>
	);
};

export default SanityImage;
